import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    ImageList,
    ImageListItem,
    Menu,
    MenuItem
} from '@mui/material'
import { ResizableBox  } from 'react-resizable'
import { ChevronLeft, Refresh, Folder } from '@mui/icons-material'
import ImageZoom from "react-image-zooom"

import { ContainerBox, CustomHandle } from '../styles/js/Dashboard.style'
import { get, post } from '../utils/http'
import LazyList from '../component/common/LazyList'
import { ClassLabel, ClustorImageData, ListMethods } from '../utils/types'
import { sortListAlphaNumeric } from '../utils/helperFunc'
import SkeletonList from '../component/common/SkeletonList'

interface ClustorProp {
    clustorInfo: {
        name: string,
        url: string
    },
    classList: ClassLabel[]
}

const RenderimageClustor = React.memo(({clustorInfo, classList}: ClustorProp) => {
    const [clustorData, setClustorData] = useState<ClustorImageData | {}>({})
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [anchorClassEl, setAnchorClassEl] = React.useState<null | HTMLElement>(null)

    const tileSize = Math.min(window.innerHeight, window.innerWidth) - 120
    const classMenuOpen = Boolean(anchorClassEl)
    const userId: number = Number(localStorage.getItem('user_id') || '1')

    const fetchClustorData = async () => {
        setIsLoading(true)
        try {
            const res = await post('ClusterImages/name', {filename: clustorInfo.name})
            setClustorData(res)
        } catch (err) {
            console.log(err)
        } finally {
            setIsLoading(false)
        }
    }

    const handleClassMenu = (event: React.MouseEvent<HTMLButtonElement>, clustor: string) => setAnchorClassEl(event.currentTarget)
    
    const handleClose = () => setAnchorClassEl(null)

    const changeClass = async (classData: ClassLabel) => {
        handleClose()
        setIsLoading(true)
        try {
            const res = await post('ClusterImages', {filename: clustorInfo.name, class_id: classData.id, user_id: userId})
            setClustorData(res)
        } catch (err) {
            console.log(err)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchClustorData()
    }, [])  // eslint-disable-line

    return (
        <ImageListItem sx={{flexDirection: 'row', gap: '1rem', width: '100%', height: '100%'}}>
            <div className='w-100 flex-grow-1' style={{height: `${tileSize}px`}}>
                <ImageZoom
                    src={clustorInfo.url}
                    alt={clustorInfo.name.split('/').pop()}
                    fullWidth={true}
                    zoom={350}
                />
            </div>
            <Box className='w-75 d-flex flex-column justify-content-center align-items-start gap-3 flex-grow-1'>
                <Typography noWrap>File Name: {clustorInfo.name.split('/').pop()}</Typography>
                <Typography className='d-flex align-items-center' noWrap>
                    Class Label: {isLoading
                        ? <CircularProgress size={20} className='ms-2' />
                        : ('class_label' in clustorData && clustorData.class_label.name)
                            ? clustorData.class_label.name
                            : 'Not assigned'
                    }
                </Typography>
                <Button
                    id="class-menu-btn"
                    className='text-capitalize'
                    onClick={(e) => handleClassMenu(e, clustorInfo.name)}
                    disabled={isLoading}
                >
                    Change Class
                </Button>
                <Menu
                    id="class-menu"
                    anchorEl={anchorClassEl}
                    open={classMenuOpen}
                    onClose={handleClose}
                    MenuListProps={{'aria-labelledby': 'class-menu-btn'}}
                >
                    {classList.map(i => (<MenuItem key={i.id} onClick={() => changeClass(i)}>{i.name}</MenuItem>))}
                </Menu>
            </Box>
        </ImageListItem>
    )
})

const ClusterExpt = () => {
    const s3 = 'pathomiq-main'

    const [clusterExptList, setClusterExptList] = useState<string[]>([])
    const [clusterExptNodeList, setClusterExptNodeList] = useState<{[x: string]: JSX.Element}[]>([])
    const [clusterImgList, setClusterImgList] = useState<{[x: string]: JSX.Element}[]>([])
    const [listIsLoading, setListIsLoading] = useState<boolean>(false)
    const [clustorIsLoading, setClusterIsLoading] = useState<boolean>(false)
    const [selectedNode, setSelectedNode] = useState<string>(`${s3}/clustering-experiments/`)
    const [clusterImages, setClusterImages] = useState<{[x: string]: string[]}>({})
    const [classList, setClassList] = useState<ClassLabel[]>([])

    const imgListRef = useRef<ListMethods>(null)

    const fetchClusterExptList = async () => {
        setListIsLoading(true)
        try {
            const res = await post('S3/node-list', { directory: selectedNode.replace(`${s3}/`, ''), s3_name: s3 })
            setClusterExptList(prevState => Array.from(new Set([...prevState, ...res])))
        } catch (err) {
            console.log(err)
        } finally {
            setListIsLoading(false)
        }
    }

    const fetchClassList = async () => {
        try {
            const res = await get('Annotationlibs/')
            if (res.length) setClassList(sortListAlphaNumeric(res))
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        fetchClusterExptList()
        fetchClassList()
    }, [])  // eslint-disable-line

    const handleSelectNode = useCallback((nodeKey: string) => {
        if (nodeKey.endsWith('/')) setSelectedNode(nodeKey)
        else {
            if (imgListRef.current) imgListRef.current.scrollToItem(nodeKey)
        }
    }, [])

    const renderClusterImages = async () => {
        setClusterIsLoading(true)
        try {
            const res = await post('S3/get-list-access', { s3_name: s3, node_list: clusterImages[selectedNode] })
            setClusterImgList([])
            setTimeout(() => {
                setClusterImgList(res.map((i: {name: string, url: string}) => ({[i.name]: <RenderimageClustor clustorInfo={i} classList={classList} />})))
            }, 0)
        } catch (err) {
            console.log(err)
        } finally {
            setClusterIsLoading(false)
        }
    }

    useEffect(() => {
        if (selectedNode in clusterImages && clusterImages[selectedNode].length) renderClusterImages()
    }, [clusterImages]) // eslint-disable-line

    useEffect(() => {
        if (clusterExptList.length) {
            let t: string[] = []
            for (let str of clusterExptList)
                if (str.startsWith(selectedNode) && str.replace(selectedNode, '').split('/').filter(i => i.length).length === 1) t.push(str)
            t = t.sort((a: string, b: string) => a.localeCompare(b, 'en', { numeric: true }))
            if (t.length) {
                if (!t[0].endsWith('/') && !(selectedNode in clusterImages)) setClusterImages({[selectedNode]: t})
                setClusterExptNodeList([])
                setTimeout(() => {
                    setClusterExptNodeList(t.map(str => ({
                        [str]: <ul
                            onClick={() => handleSelectNode(str)}
                            style={{ cursor: 'pointer' }}
                            className='fs-6 hover-color d-flex gap-2 align-items-center'
                        >
                            {str.endsWith('/') && <Folder color='primary' />}
                            {str.replace(selectedNode, '').replace('/', '')}
                        </ul>
                    })))
                }, 0)
            } else if (selectedNode.endsWith('/')) fetchClusterExptList()
        }
    }, [selectedNode, clusterExptList]) // eslint-disable-line

    const handleGoBack = () => {
        if (selectedNode === `${s3}/clustering-experiments/`) return
        let t: string[] | string = selectedNode.split('/').filter(i => i.length)
        t.pop()
        t = t.join('/') + '/'
        setSelectedNode(t)
    }

    return (
        <ContainerBox>
            <ResizableBox 
                width={400}
                height={Infinity}
                minConstraints={[400, Infinity]}
                maxConstraints={[1000, Infinity]}
                handle={<CustomHandle />}
                className='position-relative'
                style={{boxShadow: '1.5px 0 0 lightgrey'}}
            >
                <Box className='w-100 h-100 d-flex flex-column'>
                    <Box className='w-100 d-flex p-2 gap-2' sx={{boxShadow: '0 1rem 1rem -1rem lightgrey'}}>
                        <IconButton title='Go back' onClick={handleGoBack} disabled={selectedNode === `${s3}/clustering-experiments/`}>
                            <ChevronLeft />
                        </IconButton>
                        <IconButton title='Reload' onClick={fetchClusterExptList} disabled={listIsLoading}>
                            <Refresh />
                        </IconButton>
                    </Box>
                    <Box className='w-100 h-100 d-flex flex-column gap-2 p-4 flex-grow-1 overflow-auto'>
                        {listIsLoading
                            ? <SkeletonList gap={16} height={24} />
                            : clusterExptNodeList.length
                                ? <LazyList items={clusterExptNodeList} />
                                : null
                        }
                    </Box>
                </Box>
            </ResizableBox>
            <Box className='w-100 h-100 d-flex align-items-center flex-column justify-content-center overflow-hidden position-relative'>
                {clustorIsLoading
                    ? <CircularProgress />
                    : clusterImgList.length
                        ? <ImageList sx={{ width: '100%', height: '100%', padding: '2rem', transform: 'translateZ(0)' }} cols={1} gap={16}>
                            <LazyList ref={imgListRef} items={clusterImgList} batch={2} />
                        </ImageList>
                        : null
                }
            </Box>
        </ContainerBox>
    )
}

export default ClusterExpt
